import Vue from 'vue'
import { checkAchievements as fetchAchievementCheck } from '@/api/achievement.js'

/**
 * 成就解锁提醒的全局共享状态。
 *
 * 设计要点：
 * - 成就评估发生在后端，前端只需在「可能产生成就」的动作之后调一次 check；
 *   为避免短时间内重复请求，这里做了「进行中则排队重跑」的节流。
 * - 解锁浮窗挂在 TaskBlockPage（全局），因此无论用户当前在哪个子页都能弹出。
 * - 「查看成就」需要跨组件跳转：这里只置一个请求标记，
 *   由 ProfilePage 在挂载时消费，并切到「我的成就」分区 + 定位到对应成就。
 */
export const achievementStore = Vue.observable({
  /** 待展示的新解锁成就队列。 */
  queue: [],
  /** 当前浮窗展示的成就（null 表示不展示）。 */
  current: null,
  /** 是否请求打开「我的成就」页面。 */
  pendingOpen: false,
  /** 打开后需要高亮 / 定位的成就编码。 */
  focusCode: '',
  /** 是否正在请求后端。 */
  checking: false,
  /** 请求期间又来了新的触发，结束后需再跑一次。 */
  rerun: false
})

/**
 * 把新解锁的成就加入提醒队列，并立刻展示第一条。
 *
 * @param {Array} list 后端返回的新解锁成就列表
 */
export function enqueueUnlocks(list) {
  if (!Array.isArray(list) || !list.length) return
  achievementStore.queue = achievementStore.queue.concat(list)
  if (!achievementStore.current) {
    showNextUnlock()
  }
}

/** 取出队首成就展示。 */
function showNextUnlock() {
  achievementStore.current = achievementStore.queue.length ? achievementStore.queue[0] : null
  achievementStore.queue = achievementStore.queue.slice(1)
}

/** 关闭当前浮窗，并继续展示队列里的下一条（如有多条同时解锁）。 */
export function dismissUnlock() {
  achievementStore.current = null
  showNextUnlock()
}

/**
 * 「查看成就」：记录要定位的成就，置打开请求，然后关闭浮窗。
 */
export function openAchievementsFromUnlock() {
  const code = achievementStore.current ? achievementStore.current.code : ''
  achievementStore.focusCode = code || ''
  achievementStore.pendingOpen = true
  dismissUnlock()
}

/**
 * 消费「打开成就页」请求（由 ProfilePage 挂载时调用）。
 *
 * @returns {{open: boolean, focusCode: string}}
 */
export function consumeOpenRequest() {
  const open = achievementStore.pendingOpen
  const focusCode = achievementStore.focusCode
  achievementStore.pendingOpen = false
  achievementStore.focusCode = ''
  return { open, focusCode }
}

/**
 * 触发一次成就检测。
 *
 * @param {boolean} [silent] 静默模式：失败时不输出告警（默认输出）
 * @returns {Promise<void>}
 */
export async function runAchievementCheck(silent = false) {
  if (!localStorage.getItem('token')) return
  if (achievementStore.checking) {
    // 已有请求在飞：标记结束后重跑，避免丢掉这次触发带来的新成就
    achievementStore.rerun = true
    return
  }
  achievementStore.checking = true
  try {
    const res = await fetchAchievementCheck()
    if (res && res.success && Array.isArray(res.data)) {
      enqueueUnlocks(res.data)
    }
  } catch (e) {
    if (!silent) {
      // 成就检测失败不应打断主流程，静默降级
      console.warn('[achievement] 检测失败', e)
    }
  } finally {
    achievementStore.checking = false
    if (achievementStore.rerun) {
      achievementStore.rerun = false
      runAchievementCheck(true)
    }
  }
}
